/**
 * Stacked bar (100% stacked column) data transform + resolution helpers.
 *
 * Forked from grouped-bar-utils rather than shared with it: stacked bar has
 * fixed roles (Grid Variables are always Stacks/categories, Grid Values are
 * always Segment Series — no Group By swap) and a materially different
 * suppression rule (a suppressed cell hides its entire Stack), so the cell
 * shape retains `suppressed`/`baseN`, which the grouped bar transform drops.
 */
import type {
  StackedBarConfig,
  StackedBarItemCustomization,
  StackedBarSegmentCustomization,
  StackedBarCell,
} from "@store/slices/chart-config-slice";
import { interpolateColor } from "@utils/chart-utils";
import {
  getGroupedVariableLabel as getStackedVariableLabel,
  getGroupedValueLabel as getStackedValueLabel,
} from "@utils/grouped-bar-utils";

export { getStackedVariableLabel, getStackedValueLabel };
export type { StackedBarCell };

export interface StackedBarDatum {
  id: string;
  label: string;
  sourceLabel?: string;
}

export interface StackedBarSeriesDatum {
  id: string;
  label: string;
  sourceLabel?: string;
  data: StackedBarCell[];
}

/** A selectable item with the label variants used to match response rows. */
export interface StackedReconcileItem {
  id: string;
  label: string;
  matchLabels: string[];
}

export interface StackedBarTransformResult {
  /** Grid Variables — always the Stacks (category axis). */
  categories: StackedBarDatum[];
  /** Grid Values — always the Segment Series (stacked within each Stack). */
  series: StackedBarSeriesDatum[];
}

function getMetricSeriesLabel(metric: any): string {
  return (
    metric?.crossVariableLabel ||
    metric?.valueMetric ||
    metric?.timePeriodLabel ||
    ""
  );
}

function getMetricSeriesId(metric: any): string {
  return String(metric?.crossVariableId || getMetricSeriesLabel(metric) || "");
}

/**
 * Transposes the API response into stacked-bar shape. Categories are always
 * the Grid Variables (Stacks); series are always the distinct Grid Values
 * (Segment Series). Cells retain `suppressed` and `baseN` (Stack N) so a
 * suppressed cell can hide its whole Stack at render time.
 */
export function processStackedBarChartData(
  data: any[],
): StackedBarTransformResult {
  const categories: StackedBarDatum[] = data.map((item) => ({
    id: String(item.variableId ?? item.variableLabel ?? ""),
    label: item.variableLabel ?? "",
  }));

  const seriesOrder: string[] = [];
  const seriesLabelById = new Map<string, string>();
  data.forEach((item) => {
    const metrics = item.surveys?.[0]?.descriptiveMetrics ?? [];
    metrics.forEach((metric: any) => {
      const id = getMetricSeriesId(metric);
      if (id && !seriesLabelById.has(id)) {
        seriesLabelById.set(id, getMetricSeriesLabel(metric));
        seriesOrder.push(id);
      }
    });
  });

  const series: StackedBarSeriesDatum[] = seriesOrder.map((id) => ({
    id,
    label: seriesLabelById.get(id) ?? "",
    data: data.map((item): StackedBarCell => {
      const metrics = item.surveys?.[0]?.descriptiveMetrics ?? [];
      const match = metrics.find((m: any) => getMetricSeriesId(m) === id);
      // A missing entry means the backend reported no data for this
      // Stack/Segment combination — distinct from a legitimate 0% value, and
      // reason enough to drop the whole Stack (PRD: "a required Stacked Cell
      // is unavailable").
      if (!match) return { value: 0, unavailable: true };
      return {
        value: match.value != null ? match.value : 0,
        statSignificance: match.statSignificance,
        suppressed: match.suppressed,
        baseN: match.baseN,
        unavailable: match.baseN === 0,
      };
    }),
  }));

  return { categories, series };
}

function buildLabelMatcher(
  items: StackedReconcileItem[],
): Map<string, { id: string; label: string }> {
  const map = new Map<string, { id: string; label: string }>();
  items.forEach((item) => {
    item.matchLabels.forEach((ml) => {
      if (ml && !map.has(ml)) map.set(ml, { id: item.id, label: item.label });
    });
  });
  return map;
}

function reconcileItem<T extends StackedBarDatum>(
  item: T,
  matcher: Map<string, { id: string; label: string }>,
): T {
  const key = item.sourceLabel ?? item.label;
  const hit = matcher.get(key);
  return {
    ...item,
    id: hit?.id ?? item.id,
    label: hit?.label ?? item.label,
    sourceLabel: key,
  };
}

/**
 * Rewrites chart-data ids to the selector datasetElementIds (and default
 * labels to the display labels) by matching the response machine label
 * against each selectable item's label variants. Stacks always reconcile
 * against variables, Segments always against values. Idempotent —
 * `sourceLabel` preserves the original response label as the stable match key.
 */
export function reconcileStackedChartData(
  data: { categories: StackedBarDatum[]; series: StackedBarSeriesDatum[] },
  variables: StackedReconcileItem[],
  values: StackedReconcileItem[],
): { categories: StackedBarDatum[]; series: StackedBarSeriesDatum[] } {
  const stackMatcher = buildLabelMatcher(variables);
  const segmentMatcher = buildLabelMatcher(values);
  return {
    categories: data.categories.map((c) => reconcileItem(c, stackMatcher)),
    series: data.series.map((s) => reconcileItem(s, segmentMatcher)),
  };
}

/** True when two chart-data structures have identical id/label/sourceLabel. */
export function stackedChartDataEquals(
  a: { categories: StackedBarDatum[]; series: StackedBarSeriesDatum[] },
  b: { categories: StackedBarDatum[]; series: StackedBarSeriesDatum[] },
): boolean {
  const sameList = (x: StackedBarDatum[], y: StackedBarDatum[]): boolean =>
    x.length === y.length &&
    x.every(
      (item, i) =>
        item.id === y[i].id &&
        item.label === y[i].label &&
        item.sourceLabel === y[i].sourceLabel,
    );
  return sameList(a.categories, b.categories) && sameList(a.series, b.series);
}

/**
 * Remaps freshly transformed (live) data to the reconciled ids/labels using a
 * previously stored, reconciled chartData — lets the dashboard filter path
 * honor selection without re-loading the question's variable universe.
 */
export function remapLiveStackedData(
  base: StackedBarTransformResult,
  stored:
    | { categories: StackedBarDatum[]; series: StackedBarSeriesDatum[] }
    | undefined,
): StackedBarTransformResult {
  if (!stored) return base;
  const buildMap = (items: StackedBarDatum[]) => {
    const m = new Map<string, { id: string; label: string }>();
    items.forEach((it) => {
      const key = it.sourceLabel ?? it.label;
      if (key && !m.has(key)) m.set(key, { id: it.id, label: it.label });
    });
    return m;
  };
  const catMap = buildMap(stored.categories);
  const serMap = buildMap(stored.series);
  const remap = <T extends StackedBarDatum>(
    item: T,
    map: Map<string, { id: string; label: string }>,
  ): T => {
    const hit = map.get(item.label);
    return hit
      ? { ...item, id: hit.id, label: hit.label, sourceLabel: item.label }
      : item;
  };
  return {
    categories: base.categories.map((c) => remap(c, catMap)),
    series: base.series.map((s) => remap(s, serMap)),
  };
}

function labelWithOverride(original: string, override?: string): string {
  return override && override.trim() !== "" ? override : original;
}

/** Sorts items by their position in `order`; unlisted items keep base order. */
function applyOrder<T extends { id: string }>(items: T[], order: string[]): T[] {
  if (order.length === 0) return items;
  const index = new Map(order.map((id, i) => [id, i]));
  return [...items].sort(
    (a, b) =>
      (index.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (index.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  );
}

// ─── Drawer: managed item lists (Stacks / Segments sections) ───────────────

export interface StackedManagedItem {
  id: string;
  originalLabel: string;
  displayLabel: string;
  hasOverride: boolean;
  /** Always true for Segments — no visibility control is ever rendered for them. */
  visible: boolean;
}

/** Builds the ordered Stacks list — every Grid Variable always appears; hiding marks, never removes. */
export function resolveStackManagedItems(
  items: StackedBarDatum[],
  order: string[],
  customizations: Record<string, StackedBarItemCustomization>,
): StackedManagedItem[] {
  const ordered = applyOrder(
    items.map((it) => ({ id: it.id, label: it.label })),
    order,
  );
  return ordered.map(({ id, label }) => {
    const cust = customizations[id];
    const override = cust?.labelOverride;
    const hasOverride = !!(override && override.trim() !== "");
    return {
      id,
      originalLabel: label,
      displayLabel: hasOverride ? override! : label,
      hasOverride,
      visible: cust?.visible ?? true,
    };
  });
}

/** Builds the ordered Segments list — every Grid Value always appears and is always visible. */
export function resolveSegmentManagedItems(
  items: StackedBarDatum[],
  order: string[],
  customizations: Record<string, StackedBarSegmentCustomization>,
): StackedManagedItem[] {
  const ordered = applyOrder(
    items.map((it) => ({ id: it.id, label: it.label })),
    order,
  );
  return ordered.map(({ id, label }) => {
    const cust = customizations[id];
    const override = cust?.labelOverride;
    const hasOverride = !!(override && override.trim() !== "");
    return {
      id,
      originalLabel: label,
      displayLabel: hasOverride ? override! : label,
      hasOverride,
      visible: true,
    };
  });
}

/** Counts currently visible Stacks and the (always-full) Segment count, for the summary line and save gate. */
export function getStackedVisibleCounts(
  stackedBar: StackedBarConfig | undefined,
): { stacks: number; segments: number } {
  const cd = stackedBar?.chartData;
  if (!stackedBar || !cd) return { stacks: 0, segments: 0 };
  return {
    stacks: cd.categories.filter(
      (c) => stackedBar.stackCustomizations[c.id]?.visible ?? true,
    ).length,
    segments: cd.series.length,
  };
}

// ─── Render resolution (chart + preview + dashboard) ────────────────────────

export interface ResolvedStackedBarSegment {
  id: string;
  label: string;
  /** One cell per resolved (visible, reportable) Stack, in Stack order. */
  data: StackedBarCell[];
}

export interface ResolvedStackedBar {
  stackLabels: string[];
  /** Visible Stacks dropped because a required cell was suppressed. */
  suppressedStackCount: number;
  segments: ResolvedStackedBarSegment[];
}

/**
 * Applies visibility, custom order and label overrides to Stacks; applies
 * order and label overrides to Segments (always all included, never
 * filtered); and drops any Stack containing a suppressed cell entirely — a
 * suppressed Segment Series must not be identifiable and must not leave
 * enough geometry for its result to be inferred by subtraction, so the whole
 * Stack disappears rather than rendering a partial bar.
 */
export function resolveStackedBarRender(
  base: StackedBarTransformResult,
  stackedBar: StackedBarConfig | undefined,
): ResolvedStackedBar {
  if (base.categories.length === 0) {
    return { stackLabels: [], suppressedStackCount: 0, segments: [] };
  }

  if (!stackedBar) {
    return {
      stackLabels: base.categories.map((c) => c.label),
      suppressedStackCount: 0,
      segments: base.series.map((s) => ({
        id: s.id,
        label: s.label,
        data: s.data,
      })),
    };
  }

  let cats = base.categories.map((c, i) => ({ ...c, sourceIndex: i }));
  cats = cats.filter(
    (c) => stackedBar.stackCustomizations[c.id]?.visible ?? true,
  );
  cats = applyOrder(cats, stackedBar.stackOrder);

  const isStackReportable = (sourceIndex: number): boolean =>
    base.series.length > 0 &&
    base.series.every((s) => {
      const cell = s.data[sourceIndex];
      return !!cell && !cell.suppressed && !cell.unavailable;
    });
  const suppressedStackCount = cats.filter(
    (c) => !isStackReportable(c.sourceIndex),
  ).length;
  cats = cats.filter((c) => isStackReportable(c.sourceIndex));

  const segments = applyOrder([...base.series], stackedBar.segmentOrder);

  return {
    stackLabels: cats.map((c) =>
      labelWithOverride(
        c.label,
        stackedBar.stackCustomizations[c.id]?.labelOverride,
      ),
    ),
    suppressedStackCount,
    segments: segments.map((s) => ({
      id: s.id,
      label: labelWithOverride(
        s.label,
        stackedBar.segmentCustomizations[s.id]?.labelOverride,
      ),
      data: cats.map((c) => s.data[c.sourceIndex] ?? { value: 0 }),
    })),
  };
}

// ─── Segment colors ─────────────────────────────────────────────────────────
// One stable color per Segment Series, reused in every Stack — matching the
// PRD's "one stable color for a Segment Series across all Stacks."

export const STACKED_DEFAULT_PALETTE = "Multicolor";
export const STACKED_CUSTOM_PALETTE = "Custom (per-segment)";

const STACKED_MULTICOLOR_COLORS = [
  "#57bfe7", // blue
  "#57dfbe", // green
  "#ffb09c", // orange
  "#a1a7df", // purple
  "#c36179", // crimson
  "#57a68c", // dark green
  "#757679", // gray
  "#ebdd7c", // yellow
];

function paletteColorAt(
  palette: string,
  index: number,
  total: number,
  manualColors: Record<string, string>,
  id: string,
): string {
  if (palette === STACKED_CUSTOM_PALETTE) {
    const manual = manualColors[id];
    if (manual) return manual;
    return STACKED_MULTICOLOR_COLORS[index % STACKED_MULTICOLOR_COLORS.length];
  }
  if (palette === "Gradient") {
    return interpolateColor(
      "#019EDB",
      "#00CF9C",
      total > 1 ? index / (total - 1) : 0,
    );
  }
  if (palette === "Grayscale") {
    return interpolateColor(
      "#f0f0f0",
      "#404040",
      total > 1 ? index / (total - 1) : 0,
    );
  }
  return STACKED_MULTICOLOR_COLORS[index % STACKED_MULTICOLOR_COLORS.length];
}

function orderedSegmentsFromChartData(
  stackedBar: StackedBarConfig,
): Array<{ id: string; label: string }> {
  const chartData = stackedBar.chartData;
  if (!chartData) return [];
  return applyOrder(
    chartData.series.map((s) => ({ id: s.id, label: s.label })),
    stackedBar.segmentOrder,
  );
}

/** Effective color per segment id (every segment, in saved order). Used by the chart at render time. */
export function resolveStackedSegmentColors(
  stackedBar: StackedBarConfig | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  if (!stackedBar) return map;
  const segments = orderedSegmentsFromChartData(stackedBar);
  const palette = stackedBar.colorPalette ?? STACKED_DEFAULT_PALETTE;
  const manual = stackedBar.segmentManualColors ?? {};
  segments.forEach(({ id }, i) =>
    map.set(id, paletteColorAt(palette, i, segments.length, manual, id)),
  );
  return map;
}

export interface StackedSegmentColorEntry {
  id: string;
  displayName: string;
  fallbackColor: string;
}

/** Segment entries (in saved order) for the Custom-palette swatch list: display label + fallback palette color. */
export function resolveStackedSegmentColorEntries(
  stackedBar: StackedBarConfig | undefined,
): StackedSegmentColorEntry[] {
  if (!stackedBar) return [];
  const segments = orderedSegmentsFromChartData(stackedBar);
  return segments.map(({ id, label }, i) => {
    const override = stackedBar.segmentCustomizations[id]?.labelOverride;
    const displayName = override && override.trim() !== "" ? override : label;
    return {
      id,
      displayName,
      fallbackColor:
        STACKED_MULTICOLOR_COLORS[i % STACKED_MULTICOLOR_COLORS.length],
    };
  });
}
